// Lo que un diseño guarda dentro de `agent_chats.lienzo.diseno` —
// ADR-2026-09-17-el-diseno-vive-en-el-jsonb-del-lienzo.
//
// PURO y sin imports a propósito: lo lee `persistenciaDelLienzo.ts` (chat-atlas), y
// `sinCiclos.test.ts` prohíbe que algo de `pages/v2` que chat-atlas importe vuelva a
// importar chat-atlas. Normalización defensiva como el resto del lienzo: basura →
// `undefined` (un chat del Agente no tiene esta clave y nunca la gana).
//
// Qué se guarda: las imágenes PROPIAS (bases del catálogo subidas y fotos que el usuario
// sumó: no son filas de imagenes_generadas, así que el lienzo no las conoce) y qué celdas
// se SACARON del lienzo (siguen en la biblioteca del diseño y vuelven desde "Agregar
// imagen" — ADR-2026-09-17-agregar-imagen-es-la-biblioteca-del-diseno).
//
// Lo que NO se guarda: la mezcla que se está armando (ADR-2026-09-17-la-mezcla-no-se-guarda)
// y el TIPO de cada imagen. Todas las imágenes son iguales y tienen las mismas acciones
// (ADR-2026-09-17-todas-las-imagenes-tienen-las-mismas-acciones): `tipo`, `tipos` y
// `accionDeTanda` de los diseños guardados antes se ignoran y se caen en el próximo guardado.

/** El tope del nombre de un diseño: el del título de un chat (`agent_chats.title`). */
export const TITULO_DE_DISENO_MAX = 80;

/** El nombre como se guarda: sin espacios de más y acotado; `null` si queda vacío. */
export function normalizarTituloDeDiseno(texto: string): string | null {
  const t = texto.replace(/\s+/g, ' ').trim().slice(0, TITULO_DE_DISENO_MAX).trim();
  return t.length > 0 ? t : null;
}

/** El título de una imagen subida: el nombre del archivo sin extensión; "Imagen" si no queda nada. */
export function tituloDeArchivo(nombre: string): string {
  const t = nombre.replace(/\.[a-z0-9]+$/i, '').replace(/\s+/g, ' ').trim();
  return t || 'Imagen';
}

export const ACCIONES_DE_DISENO = ['en-uso', 'mezcla', 'plano', 'base-descrita'] as const;

/**
 * Qué tipo de imagen sale de una mezcla (ADR-2026-09-18-la-mezcla-elige-realista-o-como-la-base):
 * `realista`, una prenda o foto real aunque la base sea un plano técnico (lo más pedido:
 * las bases del catálogo son planos); `como-la-base`, el mismo tipo que la base (un plano
 * se edita como plano).
 */
export const RESULTADOS_DE_MEZCLA = ['realista', 'como-la-base'] as const;
export type ResultadoDeMezcla = (typeof RESULTADOS_DE_MEZCLA)[number];
export const RESULTADO_DE_MEZCLA_DEFAULT: ResultadoDeMezcla = 'realista';
export type AccionDeDiseno = (typeof ACCIONES_DE_DISENO)[number];

export interface ImagenPropia {
  id: string;
  titulo: string;
  bucket: string;
  path: string;
  /** Epoch de cuando entró al diseño. */
  creadaEn: number;
}

export interface DisenoGuardado {
  version: 1;
  imagenes: ImagenPropia[];
  /** Ids de celda (propias o filas) sacadas del lienzo: no se dibujan, pero quedan en la
   *  biblioteca del diseño para volver a sumarlas. */
  quitadas: string[];
}

export const DISENO_VACIO: DisenoGuardado = {
  version: 1,
  imagenes: [],
  quitadas: [],
};

const esTexto = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
const esObjeto = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

export function normalizarDiseno(raw: unknown): DisenoGuardado | undefined {
  if (!esObjeto(raw) || raw.version !== 1) return undefined;
  const imagenes: ImagenPropia[] = [];
  if (Array.isArray(raw.imagenes)) {
    for (const i of raw.imagenes) {
      if (!esObjeto(i) || !esTexto(i.id) || !esTexto(i.bucket) || !esTexto(i.path)) continue;
      imagenes.push({
        id: i.id,
        titulo: typeof i.titulo === 'string' ? i.titulo : '',
        bucket: i.bucket,
        path: i.path,
        creadaEn: Number.isFinite(i.creadaEn) ? (i.creadaEn as number) : 0,
      });
    }
  }
  const quitadas = Array.isArray(raw.quitadas) ? Array.from(new Set(raw.quitadas.filter(esTexto))) : [];
  return { version: 1, imagenes, quitadas };
}

export const esImagenPropia = (d: DisenoGuardado, id: string): boolean =>
  d.imagenes.some((i) => i.id === id);

export function conImagenPropia(d: DisenoGuardado, img: ImagenPropia): DisenoGuardado {
  if (esImagenPropia(d, img.id)) return sinQuitada(d, img.id);
  return { ...d, imagenes: [...d.imagenes, img] };
}

export const estaQuitada = (d: DisenoGuardado, id: string): boolean => d.quitadas.includes(id);

/** Saca la celda del lienzo; sigue en la biblioteca del diseño. */
export function conQuitada(d: DisenoGuardado, id: string): DisenoGuardado {
  return estaQuitada(d, id) ? d : { ...d, quitadas: [...d.quitadas, id] };
}

/** La vuelve a poner en el lienzo (donde estaba: su tanda no se tocó). */
export function sinQuitada(d: DisenoGuardado, id: string): DisenoGuardado {
  return estaQuitada(d, id) ? { ...d, quitadas: d.quitadas.filter((q) => q !== id) } : d;
}
